import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { ReservationFormDialog } from "@/components/admin/ReservationFormDialog";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { reservationService, roomService, roomTypeService } from "@/lib/api";
import { formatKrw } from "@/lib/format";
import { addDaysISO, assignLanes, buildDays, clipToWindow, laneCount } from "@/lib/timeline";
import { cn } from "@/lib/utils";
import { useSelectedProperty } from "@/stores/property";
import type { Reservation, ReservationCreate, ReservationUpdate, Room, RoomType } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { format, parseISO } from "date-fns";
import { ko } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

// Column width per day / row height per lane (px). Fixed so drag math is trivial.
const COL = 48;
const LANE_H = 38;
const BLOCK_H = 30;
const DAY_OPTIONS = [7, 14, 21, 30];

const STATUS_BLOCK: Record<string, string> = {
  booked: "bg-blue-500/90 hover:bg-blue-500 text-white",
  checked_in: "bg-emerald-600/90 hover:bg-emerald-600 text-white",
  checked_out: "bg-muted-foreground/40 text-white",
};

const STATUS_LABEL: Record<string, string> = {
  booked: "예약",
  checked_in: "체크인",
  checked_out: "체크아웃",
};

function todayISO(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function errorToast(error: unknown, fallback: string) {
  const detail = isAxiosError(error) ? error.response?.data?.detail : null;
  if (isAxiosError(error) && error.response?.status === 409) {
    toast.error(typeof detail === "string" ? detail : "일정이 겹치거나 가용 객실이 없습니다.");
  } else {
    toast.error(fallback);
  }
}

// ─── Drag state ───────────────────────────────────────────────────────────────

/** Rows are addressed by key: `room:<id>` (assign) or `type:<id>` (unassign). */
interface RowInfo {
  key: string;
  roomTypeId: string;
  roomId: string | null;
}

interface MoveDrag {
  kind: "move";
  reservation: Reservation;
  originKey: string;
  startX: number;
  startY: number;
  dayDelta: number;
  dy: number;
  targetKey: string | null;
  copy: boolean;
  moved: boolean;
}

interface CreateDrag {
  kind: "create";
  row: RowInfo;
  rowLeft: number;
  anchorIndex: number;
  currentIndex: number;
}

type Drag = MoveDrag | CreateDrag | null;

function rowKeyFromPoint(x: number, y: number): string | null {
  return (
    document.elementFromPoint(x, y)?.closest("[data-rowkey]")?.getAttribute("data-rowkey") ?? null
  );
}

// ─── Detail / edit dialog ─────────────────────────────────────────────────────

interface DetailDialogProps {
  reservation: Reservation | null;
  roomTypes: RoomType[];
  rooms: Room[];
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSave: (id: string, body: ReservationUpdate) => void;
  onCheckIn: (id: string, roomId: string) => void;
  onCheckOut: (id: string) => void;
  onCancel: (id: string) => void;
}

function DetailDialog({
  reservation: rsv,
  roomTypes,
  rooms,
  onOpenChange,
  isPending,
  onSave,
  onCheckIn,
  onCheckOut,
  onCancel,
}: DetailDialogProps) {
  const [guestName, setGuestName] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [roomSel, setRoomSel] = useState("unassigned");
  // Sync local form state when a new reservation is opened.
  const [lastId, setLastId] = useState<string | null>(null);
  if (rsv && rsv.id !== lastId) {
    setLastId(rsv.id);
    setGuestName(rsv.guest_name);
    setCheckIn(rsv.check_in);
    setCheckOut(rsv.check_out);
    setRoomSel(rsv.room_id ?? "unassigned");
  }

  const typeName = roomTypes.find((rt) => rt.id === rsv?.room_type_id)?.name ?? "";
  const typeRooms = rooms.filter((r) => r.is_active && r.room_type_id === rsv?.room_type_id);
  const editable = rsv?.status === "booked";
  const selectedRoomId = roomSel === "unassigned" ? null : roomSel;

  if (!rsv) return null;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>예약 상세</DialogTitle>
          <DialogDescription>
            {typeName} · {STATUS_LABEL[rsv.status] ?? rsv.status} · {formatKrw(rsv.total_amount)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tl-guest-name">게스트명</Label>
            <Input
              id="tl-guest-name"
              value={guestName}
              disabled={!editable}
              onChange={(e) => setGuestName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="tl-check-in">체크인</Label>
              <Input
                id="tl-check-in"
                type="date"
                value={checkIn}
                disabled={!editable}
                onChange={(e) => setCheckIn(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tl-check-out">체크아웃</Label>
              <Input
                id="tl-check-out"
                type="date"
                value={checkOut}
                disabled={!editable}
                onChange={(e) => setCheckOut(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tl-room">호실</Label>
            <Select value={roomSel} onValueChange={setRoomSel} disabled={!editable}>
              <SelectTrigger id="tl-room">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">미배정</SelectItem>
                {typeRooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {rsv.status === "booked" ? (
            <>
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={isPending}
                onClick={() => onCancel(rsv.id)}
              >
                예약 취소
              </Button>
              <Button
                variant="outline"
                disabled={isPending || !selectedRoomId}
                title={selectedRoomId ? undefined : "호실을 먼저 선택하세요"}
                onClick={() => selectedRoomId && onCheckIn(rsv.id, selectedRoomId)}
              >
                체크인
              </Button>
              <Button
                disabled={
                  isPending || !guestName.trim() || !checkIn || !checkOut || checkIn >= checkOut
                }
                onClick={() =>
                  onSave(rsv.id, {
                    guest_name: guestName.trim(),
                    check_in: checkIn,
                    check_out: checkOut,
                    room_id: selectedRoomId,
                  })
                }
              >
                저장
              </Button>
            </>
          ) : null}
          {rsv.status === "checked_in" ? (
            <Button variant="outline" disabled={isPending} onClick={() => onCheckOut(rsv.id)}>
              체크아웃
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TimelinePage() {
  const queryClient = useQueryClient();
  const propertyId = useSelectedProperty();

  const [startISO, setStartISO] = useState(() => addDaysISO(todayISO(), -2));
  const [dayCount, setDayCount] = useState(14);
  const days = useMemo(() => buildDays(startISO, dayCount), [startISO, dayCount]);
  const endISO = addDaysISO(startISO, dayCount);

  const [drag, setDrag] = useState<Drag>(null);
  const dragRef = useRef<Drag>(null);
  const updateDrag = (next: Drag) => {
    dragRef.current = next;
    setDrag(next);
  };

  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<{
    room_type_id: string;
    check_in: string;
    check_out: string;
    room_id: string | null;
  } | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: reservations, isLoading } = useQuery({
    queryKey: ["reservations", propertyId, startISO, dayCount],
    queryFn: () => reservationService.list(propertyId ?? "", { from: startISO, to: endISO }),
    enabled: Boolean(propertyId),
  });
  const { data: roomTypes = [] } = useQuery({
    queryKey: ["room-types", propertyId],
    queryFn: () => roomTypeService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const { data: rooms = [] } = useQuery({
    queryKey: ["rooms", propertyId],
    queryFn: () => roomService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const active = useMemo(
    () => (reservations ?? []).filter((r) => r.status !== "cancelled"),
    [reservations]
  );

  const groups = useMemo(
    () =>
      roomTypes.map((rt) => {
        const typeRooms = rooms.filter((r) => r.room_type_id === rt.id);
        const unassigned = active.filter((r) => r.room_type_id === rt.id && !r.room_id);
        return { roomType: rt, rooms: typeRooms, unassigned };
      }),
    [roomTypes, rooms, active]
  );

  const byRoom = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    for (const r of active) {
      if (r.room_id) {
        const list = map.get(r.room_id) ?? [];
        list.push(r);
        map.set(r.room_id, list);
      }
    }
    return map;
  }, [active]);

  const detail = detailId ? (active.find((r) => r.id === detailId) ?? null) : null;

  // ── Mutations ─────────────────────────────────────────────────────────────
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["reservations"] });
    queryClient.invalidateQueries({ queryKey: ["rooms"] });
    queryClient.invalidateQueries({ queryKey: ["room-type-availability"] });
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReservationUpdate }) =>
      reservationService.update(id, body),
    onSuccess: () => {
      invalidate();
      toast.success("예약이 변경되었습니다.");
    },
    onError: (error) => errorToast(error, "변경에 실패했습니다."),
  });

  const createMutation = useMutation({
    mutationFn: (body: ReservationCreate) => reservationService.create(body),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      toast.success("예약이 등록되었습니다.");
    },
    onError: (error) => errorToast(error, "등록에 실패했습니다."),
  });

  const checkInMutation = useMutation({
    mutationFn: ({ id, roomId }: { id: string; roomId: string }) =>
      reservationService.checkIn(id, roomId),
    onSuccess: () => {
      invalidate();
      setDetailId(null);
      toast.success("체크인 처리되었습니다.");
    },
    onError: (error) => errorToast(error, "체크인에 실패했습니다."),
  });

  const checkOutMutation = useMutation({
    mutationFn: (id: string) => reservationService.checkOut(id),
    onSuccess: () => {
      invalidate();
      setDetailId(null);
      toast.success("체크아웃 처리되었습니다.");
    },
    onError: (error) => errorToast(error, "체크아웃에 실패했습니다."),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => reservationService.cancel(id),
    onSuccess: () => {
      invalidate();
      setDetailId(null);
      toast.success("예약이 취소되었습니다.");
    },
    onError: (error) => errorToast(error, "취소에 실패했습니다."),
  });

  const mutationPending =
    updateMutation.isPending ||
    createMutation.isPending ||
    checkInMutation.isPending ||
    checkOutMutation.isPending ||
    cancelMutation.isPending;

  // ── Row registry (drop targets) ───────────────────────────────────────────
  const rowInfos = useMemo(() => {
    const map = new Map<string, RowInfo>();
    for (const g of groups) {
      map.set(`type:${g.roomType.id}`, {
        key: `type:${g.roomType.id}`,
        roomTypeId: g.roomType.id,
        roomId: null,
      });
      for (const room of g.rooms) {
        map.set(`room:${room.id}`, {
          key: `room:${room.id}`,
          roomTypeId: g.roomType.id,
          roomId: room.id,
        });
      }
    }
    return map;
  }, [groups]);

  // ── Block drag (move / copy) ──────────────────────────────────────────────
  const onBlockPointerDown = (e: ReactPointerEvent, rsv: Reservation, originKey: string) => {
    if (e.button !== 0) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    updateDrag({
      kind: "move",
      reservation: rsv,
      originKey,
      startX: e.clientX,
      startY: e.clientY,
      dayDelta: 0,
      dy: 0,
      targetKey: originKey,
      copy: e.shiftKey || e.metaKey,
      moved: false,
    });
  };

  const onBlockPointerMove = (e: ReactPointerEvent) => {
    const d = dragRef.current;
    if (d?.kind !== "move") return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const moved = d.moved || Math.abs(dx) > 4 || Math.abs(dy) > 4;
    const draggable = d.reservation.status === "booked";
    updateDrag({
      ...d,
      moved,
      dayDelta: draggable ? Math.round(dx / COL) : 0,
      dy: draggable ? dy : 0,
      targetKey: draggable ? (rowKeyFromPoint(e.clientX, e.clientY) ?? d.targetKey) : d.originKey,
      copy: e.shiftKey || e.metaKey,
    });
  };

  const onBlockPointerUp = () => {
    const d = dragRef.current;
    updateDrag(null);
    if (d?.kind !== "move") return;
    if (!d.moved) {
      setDetailId(d.reservation.id);
      return;
    }
    if (d.reservation.status !== "booked") return;
    const target = d.targetKey ? rowInfos.get(d.targetKey) : undefined;
    const sameRow = d.targetKey === d.originKey;
    if ((!target && !sameRow) || (d.dayDelta === 0 && sameRow && !d.copy)) return;
    const rsv = d.reservation;
    const check_in = addDaysISO(rsv.check_in, d.dayDelta);
    const check_out = addDaysISO(rsv.check_out, d.dayDelta);
    const roomTypeId = target?.roomTypeId ?? rsv.room_type_id;
    const roomId = target ? target.roomId : rsv.room_id;
    if (d.copy) {
      if (!propertyId) return;
      // 단체(그룹) 예약: 대표 예약을 원하는 칸으로 복사 — 타 타입/호실로도 가능.
      createMutation.mutate({
        property_id: propertyId,
        room_type_id: roomTypeId,
        check_in,
        check_out,
        guest_name: rsv.guest_name,
        room_id: roomId,
      });
    } else {
      updateMutation.mutate({
        id: rsv.id,
        body: { check_in, check_out, room_type_id: roomTypeId, room_id: roomId },
      });
    }
  };

  // ── Empty-cell drag (create) ──────────────────────────────────────────────
  const cellIndexAt = (clientX: number, rowLeft: number) =>
    Math.max(0, Math.min(dayCount - 1, Math.floor((clientX - rowLeft) / COL)));

  const onCellsPointerDown = (e: ReactPointerEvent, row: RowInfo) => {
    if (e.button !== 0) return;
    // Blocks sit above cells — reaching here means an empty area was pressed.
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    const rowLeft = (e.currentTarget as Element).getBoundingClientRect().left;
    const idx = cellIndexAt(e.clientX, rowLeft);
    updateDrag({ kind: "create", row, rowLeft, anchorIndex: idx, currentIndex: idx });
  };

  const onCellsPointerMove = (e: ReactPointerEvent) => {
    const d = dragRef.current;
    if (d?.kind !== "create") return;
    updateDrag({ ...d, currentIndex: cellIndexAt(e.clientX, d.rowLeft) });
  };

  const onCellsPointerUp = () => {
    const d = dragRef.current;
    updateDrag(null);
    if (d?.kind !== "create") return;
    const lo = Math.min(d.anchorIndex, d.currentIndex);
    const hi = Math.max(d.anchorIndex, d.currentIndex);
    setCreateDefaults({
      room_type_id: d.row.roomTypeId,
      check_in: days[lo],
      check_out: addDaysISO(days[hi], 1),
      room_id: d.row.roomId,
    });
    setCreateOpen(true);
  };

  const handleCreate = (values: Omit<ReservationCreate, "property_id">) => {
    if (!propertyId) return;
    createMutation.mutate({
      ...values,
      property_id: propertyId,
      // Only keep the pre-picked room if the type wasn't changed in the form.
      room_id:
        createDefaults && values.room_type_id === createDefaults.room_type_id
          ? createDefaults.room_id
          : null,
    });
  };

  // ── Rendering ─────────────────────────────────────────────────────────────
  const renderBlock = (rsv: Reservation, lane: number, rowKey: string) => {
    const clip = clipToWindow({ start: rsv.check_in, end: rsv.check_out }, startISO, dayCount);
    if (!clip) return null;
    const dragging = drag?.kind === "move" && drag.reservation.id === rsv.id && drag.moved;
    return (
      <button
        type="button"
        key={rsv.id}
        data-testid={`block-${rsv.id}`}
        className={cn(
          "absolute z-10 flex items-center gap-1 truncate rounded-md px-2 text-xs font-medium shadow-sm outline-none",
          STATUS_BLOCK[rsv.status] ?? "bg-muted-foreground/40 text-white",
          rsv.status === "booked" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          clip.clippedStart && "rounded-l-none",
          clip.clippedEnd && "rounded-r-none",
          dragging && (drag.copy ? "z-50 ring-2 ring-emerald-400" : "z-50 ring-2 ring-primary")
        )}
        style={{
          left: clip.startIndex * COL + 2,
          width: clip.nights * COL - 4,
          top: lane * LANE_H + (LANE_H - BLOCK_H) / 2,
          height: BLOCK_H,
          touchAction: "none",
          transform: dragging ? `translate(${drag.dayDelta * COL}px, ${drag.dy}px)` : undefined,
          opacity: dragging ? 0.85 : undefined,
          // While dragging, let elementFromPoint see the row underneath;
          // pointer capture keeps delivering events to this block.
          pointerEvents: dragging ? "none" : undefined,
        }}
        onPointerDown={(e) => onBlockPointerDown(e, rsv, rowKey)}
        onPointerMove={onBlockPointerMove}
        onPointerUp={onBlockPointerUp}
      >
        <span className="truncate">{rsv.guest_name || "(이름 없음)"}</span>
        {dragging && drag.copy ? <span className="shrink-0">+복사</span> : null}
      </button>
    );
  };

  const renderCells = (row: RowInfo, lanes: number) => {
    const isCreateRow = drag?.kind === "create" && drag.row.key === row.key;
    const lo = isCreateRow ? Math.min(drag.anchorIndex, drag.currentIndex) : -1;
    const hi = isCreateRow ? Math.max(drag.anchorIndex, drag.currentIndex) : -1;
    const isDropTarget = drag?.kind === "move" && drag.moved && drag.targetKey === row.key;
    return (
      <div
        data-rowkey={row.key}
        className={cn("relative", isDropTarget && "bg-primary/10")}
        style={{ width: dayCount * COL, height: lanes * LANE_H, touchAction: "none" }}
        onPointerDown={(e) => onCellsPointerDown(e, row)}
        onPointerMove={onCellsPointerMove}
        onPointerUp={onCellsPointerUp}
      >
        {days.map((day, i) => (
          <div
            key={day}
            className={cn(
              "absolute inset-y-0 border-r border-border/60",
              day === todayISO() && "bg-primary/5",
              isCreateRow && i >= lo && i <= hi && "bg-blue-100/70"
            )}
            style={{ left: i * COL, width: COL }}
          />
        ))}
      </div>
    );
  };

  if (!propertyId) return <NoPropertySelected />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">타임라인</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            드래그로 일정·호실 변경, 빈 칸 드래그로 예약 생성, Shift+드래그로 복사(단체 예약).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="이전 주"
            onClick={() => setStartISO((s) => addDaysISO(s, -7))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            className="w-40"
            value={startISO}
            onChange={(e) => e.target.value && setStartISO(e.target.value)}
          />
          <Button
            variant="outline"
            size="icon"
            aria-label="다음 주"
            onClick={() => setStartISO((s) => addDaysISO(s, 7))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setStartISO(addDaysISO(todayISO(), -2))}>
            오늘
          </Button>
          <Select value={String(dayCount)} onValueChange={(v) => setDayCount(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}일
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <BrandLoader className="py-12" size={44} label="불러오는 중…" />
          ) : (
            <div className="overflow-x-auto rounded-xl">
              <div className="min-w-max select-none">
                {/* Date header */}
                <div className="flex border-b bg-background">
                  <div className="sticky left-0 z-20 w-40 shrink-0 border-r bg-background px-3 py-2 text-xs font-medium text-muted-foreground">
                    호실 / 날짜
                  </div>
                  <div className="relative" style={{ width: dayCount * COL, height: 44 }}>
                    {days.map((day, i) => {
                      const d = parseISO(day);
                      const dow = d.getDay();
                      return (
                        <div
                          key={day}
                          className={cn(
                            "absolute inset-y-0 flex flex-col items-center justify-center border-r border-border/60 text-xs",
                            day === todayISO() && "bg-primary/5 font-semibold"
                          )}
                          style={{ left: i * COL, width: COL }}
                        >
                          <span className="tabular-nums">{format(d, "M.d")}</span>
                          <span
                            className={cn(
                              "text-[10px]",
                              dow === 0
                                ? "text-red-500"
                                : dow === 6
                                  ? "text-blue-500"
                                  : "text-muted-foreground"
                            )}
                          >
                            {format(d, "EEE", { locale: ko })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {groups.length === 0 ? (
                  <p className="px-6 py-10 text-sm text-muted-foreground">
                    객실 타입을 먼저 등록하세요.
                  </p>
                ) : (
                  groups.map(({ roomType, rooms: typeRooms, unassigned }) => {
                    const typeRow = rowInfos.get(`type:${roomType.id}`) as RowInfo;
                    const unassignedLanes = assignLanes(
                      unassigned.map((r) => ({ start: r.check_in, end: r.check_out, r }))
                    );
                    const typeLaneCount = laneCount(unassignedLanes);
                    return (
                      <div key={roomType.id}>
                        {/* Room-type row: unassigned reservations live here */}
                        <div className="flex border-b bg-muted/50">
                          <div
                            className="sticky left-0 z-20 w-40 shrink-0 border-r bg-muted px-3 py-2 text-sm font-medium"
                            style={{ minHeight: typeLaneCount * LANE_H }}
                          >
                            {roomType.name}
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              미배정 {unassigned.length}
                            </span>
                          </div>
                          <div className="relative">
                            {renderCells(typeRow, typeLaneCount)}
                            {unassignedLanes.map(({ item, lane }) =>
                              renderBlock(item.r, lane, typeRow.key)
                            )}
                          </div>
                        </div>
                        {/* Physical room rows */}
                        {typeRooms.map((room) => {
                          const rowKey = `room:${room.id}`;
                          const row = rowInfos.get(rowKey) as RowInfo;
                          const blocks = assignLanes(
                            (byRoom.get(room.id) ?? []).map((r) => ({
                              start: r.check_in,
                              end: r.check_out,
                              r,
                            }))
                          );
                          const lanes = laneCount(blocks);
                          return (
                            <div key={room.id} className="flex border-b last:border-b-0">
                              <div
                                className="sticky left-0 z-20 flex w-40 shrink-0 items-center gap-1.5 border-r bg-background px-3 text-sm"
                                style={{ minHeight: lanes * LANE_H }}
                              >
                                <span className="truncate">{room.name}</span>
                                {!room.is_active ? (
                                  <span className="text-[10px] text-muted-foreground">
                                    (비활성)
                                  </span>
                                ) : null}
                              </div>
                              <div className="relative">
                                {renderCells(row, lanes)}
                                {/* Blackout (OOO) spans */}
                                {(room.blackouts ?? []).map((b) => {
                                  const clip = clipToWindow(b, startISO, dayCount);
                                  if (!clip) return null;
                                  return (
                                    <div
                                      key={`${b.start}-${b.end}`}
                                      title={b.reason ?? "사용불가"}
                                      className="pointer-events-none absolute inset-y-0 z-[5] bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(120,120,120,0.25)_4px,rgba(120,120,120,0.25)_8px)]"
                                      style={{
                                        left: clip.startIndex * COL,
                                        width: clip.nights * COL,
                                      }}
                                    />
                                  );
                                })}
                                {blocks.map(({ item, lane }) => renderBlock(item.r, lane, rowKey))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-blue-500/90" /> 예약
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-emerald-600/90" /> 체크인
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-muted-foreground/40" /> 체크아웃
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(120,120,120,0.4)_3px,rgba(120,120,120,0.4)_6px)]" />
          사용불가(OOO)
        </span>
        <span>· 타입 행으로 드래그하면 미배정으로 전환됩니다.</span>
      </div>

      <ReservationFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        roomTypes={roomTypes}
        isPending={createMutation.isPending}
        defaults={createDefaults ?? undefined}
        onSubmit={handleCreate}
      />

      <DetailDialog
        reservation={detail}
        roomTypes={roomTypes}
        rooms={rooms}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
        isPending={mutationPending}
        onSave={(id, body) =>
          updateMutation.mutate({ id, body }, { onSuccess: () => setDetailId(null) })
        }
        onCheckIn={(id, roomId) => checkInMutation.mutate({ id, roomId })}
        onCheckOut={(id) => checkOutMutation.mutate(id)}
        onCancel={(id) => cancelMutation.mutate(id)}
      />
    </div>
  );
}
